import React, {useState, useEffect, useContext} from 'react'
import {useTranslation} from 'react-i18next'
import {getStaticSlug} from './common/StaticSlug'
import {ConfigContext, ContentBody} from '@dudagroup/editor'

interface PreviewLinkCopyProps {
  content: ContentBody
  isMedia?: boolean
  defaultLang: string
}

function getDefaultContentLink(content: ContentBody, websiteUrl: string, lang: string) {
  return `${websiteUrl}/${lang}/${content.id}/${getStaticSlug(content.contentType, lang)}/${
    content.slugI18n[lang]
  }`
}

function getMediaLink(content: ContentBody, lang: string, languages: string[]) {
  const settings = document.getElementById('settings')
  let media = content.content?.media?.[lang]?.media

  if (!media) {
    for (const lang of languages) {
      if (content.content?.media?.[lang]?.media) {
        media = content.content?.media?.[lang]?.media
        break
      }
    }
  }

  if (!media) {
    return 'no media found'
  }

  if (settings?.textContent && media?.filename && media?.extension) {
    const {mediaPreviewUrl} = JSON.parse(settings.textContent)

    if (
      content.meta.robotsMetaDirectives === 'indexFollow' ||
      content.meta.robotsMetaDirectives === 'indexNoFollow' ||
      !content.meta.robotsMetaDirectives
    ) {
      return `${mediaPreviewUrl}/media/${lang}/${content.id}/${media.filename}${media.extension}`
    }
    return `${mediaPreviewUrl}/media-noindex/${lang}/${content.id}/${media.filename}${media.extension}`
  }
  return ''
}

export default function PreviewLinkCopy({content, isMedia, defaultLang}: PreviewLinkCopyProps) {
  const {t} = useTranslation()
  const {languages, defaultLanguageTag} = useContext(ConfigContext).apiConfig.languages
  // Only the tenant's enabled languages; the Languages enum lists the server-wide set.
  const tenantLanguages = languages.map((language) => language.tag)
  const [selectedLang, setSelectedLang] = useState(
    tenantLanguages.includes(defaultLang) ? defaultLang : defaultLanguageTag
  )
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const settings = document.getElementById('settings')
    if (settings?.textContent) {
      try {
        const parsedSettings = JSON.parse(settings.textContent)
        setWebsiteUrl(parsedSettings.websiteUrl)
      } catch (err) {
        console.error('Error parsing settings:', err)
      }
    }
  }, [])

  if (!websiteUrl) return null

  const handleCopy = () => {
    const url = isMedia
      ? getMediaLink(content, selectedLang, tenantLanguages)
      : getDefaultContentLink(content, websiteUrl, selectedLang)

    if (!url) {
      console.error('No url found')
      return
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        })
        .catch((err) => {
          console.error('Failed to copy: ', err)
        })
    } else {
      const textArea = document.createElement('textarea')
      textArea.value = url
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err)
      }
      document.body.removeChild(textArea)
    }
  }

  return (
    <div style={{display: 'flex', alignItems: 'center'}}>
      {tenantLanguages.length >= 2 && (
        <select
          onChange={(e) => setSelectedLang(e.target.value)}
          value={selectedLang}
          style={{border: '1px solid', borderRadius: '5px', marginRight: '10px'}}>
          {tenantLanguages.map((lang) => (
            <option key={lang} value={lang}>
              {lang.toUpperCase()}
            </option>
          ))}
        </select>
      )}
      <button style={{padding: '2px 7px'}} className="rs-btn rs-btn-primary" onClick={handleCopy}>
        {!copied ? t('global.buttons.linkCopy') : t('global.buttons.linkCopied')}
      </button>
    </div>
  )
}
